import React, { useEffect, useMemo, useRef, useState } from "react";
import FindOfficeBanner from "./FindOfficeBanner.jsx";
import FindOfficePagination from "./FindOfficePagination.jsx";
import DynamicOfficeCard from "../DynamicOfficeCard/DynamicOfficeCard.jsx";
import MapLocations from "../Map/MapLocations.jsx";
import DOMPurify from "dompurify";

const DEFAULT_PAGE_SIZE = 10;

const FindOffice = ({ bannerProps, paginationProps, defaultFormProps }) => {
  const rootRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all"); // "all" | "stewart" | "independent"
  const [currentPage, setCurrentPage] = useState(1);
  const [didSearch, setDidSearch] = useState(false);

  const pageSize = paginationProps?.pageSize ?? DEFAULT_PAGE_SIZE;

  // Only show the pagination block when there are results or we're loading or there was an error
  const showPagination = isLoading || didSearch || !!error;

  // Wire up custom events from the co-located plain form script (findOfficeSubmit.js)
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const form = root.querySelector("form.find-office-form");
    if (!form) return;

    const onLoading = e => {
      const loading = !!e.detail?.loading;
      setIsLoading(loading);
      if (loading) {
        setError(null);
      }
    };

    const onResults = e => {
      const list = Array.isArray(e.detail?.list) ? e.detail.list : [];
      setLocations(list);
      setIsLoading(false);
      setError(null);
      setActiveFilter("all");
      setCurrentPage(1);
      setDidSearch(true);
    };

    const onError = e => {
      const status = e.detail?.status;

      if (status === 404) {
        setLocations([]);
        setIsLoading(false);
        setError(null);
        setActiveFilter("all");
        setCurrentPage(1);
        setDidSearch(true);
        return;
      }

      setIsLoading(false);
      setError(e.detail?.message || "Unknown error");
      setDidSearch(true);
    };

    form.addEventListener("findoffice:loading", onLoading);
    form.addEventListener("findoffice:results", onResults);
    form.addEventListener("findoffice:error", onError);

    return () => {
      form.removeEventListener("findoffice:loading", onLoading);
      form.removeEventListener("findoffice:results", onResults);
      form.removeEventListener("findoffice:error", onError);
    };
  }, []);

  const hasLocations = locations.length > 0;

  /* Normalize incoming locations once, keep original order index for stable secondary sort */
  const normalized = useMemo(
    () =>
      locations.map((loc, idx) => ({
        ...loc,
        __idx: idx,
        __isStewart: loc.AgentType !== "I",
      })),
    [locations],
  );

  /* Filter by current ownership tab */
  const filtered = useMemo(() => {
    if (activeFilter === "stewart")
      return normalized.filter(l => l.__isStewart);
    if (activeFilter === "independent")
      return normalized.filter(l => !l.__isStewart);
    return normalized; // "all"
  }, [normalized, activeFilter]);

  /* When "all", prioritize Stewart first, preserving original relative order */
  const prioritized = useMemo(() => {
    if (activeFilter !== "all") return filtered;
    return [...filtered].sort(
      (a, b) => b.__isStewart - a.__isStewart || a.__idx - b.__idx,
    );
  }, [filtered, activeFilter]);

  /* Pagination math */
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(prioritized.length / pageSize)),
    [prioritized.length, pageSize],
  );

  // Ensure current page never exceeds total pages (e.g., when results shrink)
  useEffect(() => {
    setCurrentPage(p => Math.min(p, totalPages));
  }, [totalPages]);

  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;

  const pagedLocations = useMemo(
    () => prioritized.slice(start, end),
    [prioritized, start, end],
  );

  // Handlers
  const handleFilterChange = next => {
    setActiveFilter(next);
    setCurrentPage(1);
  };

  const handlePageChange = page => setCurrentPage(page);

  // For the map we may want the full prioritized list (not paginated)
  const locationsForMap = prioritized;

  // Generate schema markup for all prioritized offices
  const renderSchemaScript = () => {
    if (!prioritized.length) return null;

    const schemaObject = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      mainEntity: {
        "@type": "ItemList",
        name: defaultFormProps?.defaultCity || "",
        itemListElement: prioritized.map((office, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: {
            "@type": "LocalBusiness",
            name: office.OfficeCompanyName || "",
            address: {
              "@type": "PostalAddress",
              streetAddress: office.OfficeAddress?.Address1 || "",
              addressLocality: office.OfficeAddress?.City || "",
              addressRegion: office.OfficeAddress?.State || "",
              postalCode: office.OfficeAddress?.Zip || "",
              addressCountry: "US",
            },
            telephone: office.Phone || "",
            faxNumber: office.Fax || "",
          }
        })),
      },
    };
    const sanitizedSchemaObject = DOMPurify.sanitize(JSON.stringify(schemaObject));
    return (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: sanitizedSchemaObject }}
      />
    );
  };

  return (
    <div className="find-office" ref={rootRef}>
      {renderSchemaScript()}
      {bannerProps && (
        <FindOfficeBanner {...bannerProps} {...defaultFormProps} />
      )}
      {showPagination && (
        <FindOfficePagination
          {...paginationProps}
          isLoading={isLoading}
          loadingMessage={paginationProps?.loadingMessage || "Searching..."}
          noMatchTitle={paginationProps?.noAgenciesMatchTitle || "No Match"}
          noMatchMessage={
            paginationProps?.noAgenciesMatchMessage ||
            "Try expanding your search criteria."
          }
          emptyTitle={paginationProps?.emptyTitle || "No Locations Found."}
          emptySubtitle={
            paginationProps?.emptySubtitle ||
            "Try expanding your search criteria."
          }
          locations={locations}
          visibleCount={prioritized.length}
          activeFilter={activeFilter}
          onFilterChange={handleFilterChange}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
      <div className="find-office-list" hidden={!hasLocations && !isLoading}>
        <DynamicOfficeCard
          {...defaultFormProps}
          offices={pagedLocations}
          officeType={paginationProps?.filters}
          phoneLabel={paginationProps?.phoneLabel || "ph."}
          faxLabel={paginationProps?.faxLabel || "fx."}
          mapLabel={paginationProps?.mapLabel || "Map"}
          detailsLabel={paginationProps?.detailsLabel || "Details"}
        />
      </div>
      {/* List/Map view toggle can control this container's "hidden" externally */}
      <div className="find-office-map" hidden>
        <MapLocations
          locations={locationsForMap}
          apiKey="AIzaSyBqsinAy2Itg1XuDwn1nI10gtxaf_9TEpU"
          mapId="df8d2d0783111f6decedefb3"
          cluster
          fitToMarkers
          officeType={paginationProps?.filters}
          phoneLabel={paginationProps?.phoneLabel || "ph."}
          faxLabel={paginationProps?.faxLabel || "fx."}
          mapLabel={paginationProps?.mapLabel || "Map"}
          detailsLabel={paginationProps?.detailsLabel || "Details"}
        />
      </div>
    </div>
  );
};

/* Normalize API "StewartOwned" field that may arrive as "true"/"false" strings or booleans */
function toBool(v) {
  return v === true || v === "true";
}

export default FindOffice;
